/**
 * History Page Component
 * 
 * Shows all past search jobs with status, results count,
 * and options to view results or delete jobs.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listJobs, deleteJob } from '../utils/api';

function HistoryPage({ onViewResults }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await listJobs({ limit: 50 });
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!confirm('Are you sure you want to delete this search and all its results?')) return;
    
    setDeletingId(jobId);
    try {
      await deleteJob(jobId);
      setJobs(jobs.filter(j => j.job_id !== jobId));
      setTotal(total - 1);
    } catch (err) {
      console.error('Failed to delete job:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/10 text-green-400 border-green-500/20',
      running: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      failed: 'bg-red-500/10 text-red-400 border-red-500/20',
      pending: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button 
            onClick={() => navigate('/search')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 text-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <h2 className="text-2xl font-bold text-white">Search History</h2>
          <p className="text-gray-400 mt-1">{total} total searches</p>
        </div>
        <button
          onClick={loadJobs}
          className="btn-secondary text-sm flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="glass rounded-2xl p-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-white mb-2">No Searches Yet</h3>
          <p className="text-gray-400 mb-6">Start your first lead search to see history here.</p>
          <Link to="/" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 inline-block font-medium transition-colors">
            Start Searching
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div
              key={job.job_id}
              className="glass rounded-xl p-5 hover-lift transition-all duration-200"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Job Info */}
                <div className="flex items-start gap-4">
                  {/* Status Dot */}
                  <div className={`mt-1.5 w-3 h-3 rounded-full flex-shrink-0 ${
                    job.status === 'completed' ? 'bg-green-500' :
                    job.status === 'running' ? 'bg-yellow-500 animate-pulse' :
                    job.status === 'failed' ? 'bg-red-500' :
                    'bg-gray-500'
                  }`} />
                  
                  <div>
                    <h4 className="text-white font-semibold text-base">
                      {job.business_type}
                      <span className="text-gray-500 font-normal"> in </span>
                      {job.city}
                    </h4>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                      <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${getStatusBadge(job.status)}`}>
                        {job.status}
                      </span>
                      <span className="text-gray-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {job.total_results} leads
                      </span>
                      <span className="text-gray-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatDate(job.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  {job.status === 'completed' && onViewResults && (
                    <button
                      onClick={() => onViewResults(job.job_id, { business_type: job.business_type, city: job.city, country: job.country, location: job.location })}
                      className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Results
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(job.job_id)}
                    disabled={deletingId === job.job_id}
                    className="px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all text-xs flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {deletingId === job.job_id ? (
                      <div className="spinner w-4 h-4 border-2 border-red-500/30 border-t-red-500" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                    Delete
                  </button>
                </div>
              </div>

              {/* Progress bar for running jobs */}
              {job.status === 'running' && (
                <div className="mt-4">
                  <div className="w-full bg-zinc-700 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 progress-bar-fill"
                      style={{ width: `${job.progress_percent || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{job.progress_percent || 0}% complete</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
